import type { MenuAnchor } from "@rhema/ui";
import { type KeyboardEvent, type MouseEvent, type PointerEvent, useEffect, useRef } from "react";
import { hapticTick } from "../platform/haptic";

const HOLD_MS = 450;

export type OpenActions = (id: string, at: MenuAnchor, opener: HTMLElement) => void;

/** The message whose actions are open, where they were asked for, and the element to give focus back to. */
export type ActionsOpen = { id: string; at: MenuAnchor; opener: HTMLElement };

/** Holds back the phone's own text selection and callout while a press might become a long press. */
function holdSelection(el: HTMLElement, hold: boolean) {
  const style = el.style as CSSStyleDeclaration & { webkitUserSelect: string; webkitTouchCallout: string };
  style.webkitUserSelect = hold ? "none" : "";
  style.userSelect = hold ? "none" : "";
  style.webkitTouchCallout = hold ? "none" : "";
}

// Opens message actions on a long press, a right click, or the keyboard (Enter, the context menu
// key, or Shift and F10) while the message has focus. A right click opens them at the pointer, and
// the keyboard under the message.
/** Runs a message's shortcut for a key pressed on it. Answers whether one ran. */
export type MessageShortcut = (id: string, e: KeyboardEvent<HTMLElement>) => boolean;

export function useMessageActions(open: OpenActions, shortcut?: MessageShortcut) {
  const timer = useRef<number | undefined>(undefined);
  const start = useRef<{ x: number; y: number } | null>(null);
  const pressed = useRef<HTMLElement | null>(null);
  // A long press opens the menu, and the click that follows it must not also open a photo or link.
  const swallowClick = useRef(false);
  // Whether the current press already opened the menu.
  const held = useRef(false);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const cancel = () => {
    window.clearTimeout(timer.current);
    start.current = null;
    if (pressed.current) holdSelection(pressed.current, false);
    pressed.current = null;
  };

  return (id: string) => ({
    tabIndex: 0,
    "aria-haspopup": "menu" as const,
    onPointerDown: (e: PointerEvent<HTMLElement>) => {
      if (e.pointerType === "mouse") return;
      const el = e.currentTarget;
      swallowClick.current = false;
      held.current = false;
      start.current = { x: e.clientX, y: e.clientY };
      pressed.current = el;
      holdSelection(el, true);
      const at = { x: e.clientX, y: e.clientY };
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        swallowClick.current = true;
        held.current = true;
        hapticTick();
        open(id, at, el);
      }, HOLD_MS);
    },
    onPointerMove: (e: PointerEvent) => {
      // A scroll is not a press.
      if (start.current && Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) > 8) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onPointerLeave: cancel,
    onContextMenu: (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      // A long press already opened the menu. The phone's own context menu event adds nothing.
      if (held.current) return;
      cancel();
      open(id, { x: e.clientX, y: e.clientY }, e.currentTarget);
    },
    onClickCapture: (e: MouseEvent) => {
      if (!swallowClick.current) return;
      swallowClick.current = false;
      e.preventDefault();
      e.stopPropagation();
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter" || e.key === "ContextMenu" || (e.key === "F10" && e.shiftKey)) {
        e.preventDefault();
        const box = e.currentTarget.getBoundingClientRect();
        open(id, { x: box.left + 16, y: box.bottom + 6 }, e.currentTarget);
        return;
      }
      // The shortcuts shown in the menu work on the focused message without opening it.
      if (shortcut?.(id, e)) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
  });
}
